import { useCallback, useMemo, useState } from 'react';
import type { LoteDaSincronizacao } from '../../api/sincronizacao.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { listarLotes } from '../../api/sincronizacao.js';
import type { FiltroDeLotes } from '../../api/sincronizacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { filialDoFiltro, rotuloDaFilial } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import { CampoFilial, ContadorRegistros, FiltroSelecao } from '../../componentes/Filtros.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';
import { IconeFila } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import {
  OPCOES_DE_TIPO,
  rotuloDoTipoDeLote,
  STATUS_DO_LOTE,
  TIPOS_DE_LOTE,
  TODOS,
  valorDoSeletor,
} from './situacao.js';

const OPCOES_DE_STATUS: readonly OpcaoFiltro[] = [
  { valor: TODOS, rotulo: 'Status · todos' },
  { valor: 'Recebido', rotulo: 'Recebidos' },
  { valor: 'Confirmado', rotulo: 'Confirmados' },
];

const NUMERO = new Intl.NumberFormat('pt-BR');

function StatusDoLoteNaLinha({ lote }: { readonly lote: LoteDaSincronizacao }) {
  return (
    <span className="sincronizacao__pilha">
      {lote.status === 'Confirmado' ? (
        <ChipStatus tom="concluida" rotulo="Confirmado" tamanho="tabela" />
      ) : (
        <ChipStatus tom="andamento" rotulo="Recebido" tamanho="tabela" />
      )}
      {lote.ultimoErro !== null && (
        <>
          <ChipStatus tom="falha" rotulo="Falha ao confirmar" tamanho="tabela" />
          <span className="sincronizacao__discreto">{lote.ultimoErro}</span>
        </>
      )}
      {lote.tentativasConfirmacao > 1 && (
        <span className="sincronizacao__discreto">{lote.tentativasConfirmacao} tentativas</span>
      )}
    </span>
  );
}

const COLUNAS: readonly Coluna<LoteDaSincronizacao>[] = [
  {
    chave: 'lote',
    rotulo: 'Lote',
    largura: '14%',
    celula: (lote) => (
      <span className="sincronizacao__pilha">
        <span className="sincronizacao__codigo">Nº {lote.numeroLote}</span>
        <span className="sincronizacao__discreto">{rotuloDoTipoDeLote(lote.tipo)}</span>
      </span>
    ),
  },
  {
    chave: 'origem',
    rotulo: 'Filial · integração',
    largura: '15%',
    celula: (lote) => (
      <span className="sincronizacao__pilha">
        <span>{rotuloDaFilial(lote.codFil)}</span>
        <span className="sincronizacao__discreto mono">
          Integração {lote.identificadorIntegracao}
        </span>
      </span>
    ),
  },
  {
    chave: 'registros',
    rotulo: 'Registros',
    largura: '86px',
    alinhamento: 'direita',
    celula: (lote) => (
      <span className="quantidade" aria-label="Registros">
        {NUMERO.format(lote.quantidadeRegistros)}
      </span>
    ),
  },
  {
    chave: 'excecoes',
    rotulo: 'Exceções',
    largura: '86px',
    alinhamento: 'direita',
    celula: (lote) => (
      <span
        className={lote.quantidadeExcecoes > 0 ? 'quantidade quantidade--destaque' : 'quantidade'}
        aria-label="Exceções"
      >
        {NUMERO.format(lote.quantidadeExcecoes)}
      </span>
    ),
  },
  {
    chave: 'status',
    rotulo: 'Status',
    largura: '22%',
    celula: (lote) => <StatusDoLoteNaLinha lote={lote} />,
  },
  {
    chave: 'datas',
    rotulo: 'Datas',
    celula: (lote) => (
      <dl className="sincronizacao__datas">
        <div>
          <dt>Recebido em</dt>
          <dd>{formatarDataHora(lote.recebidoEm)}</dd>
        </div>
        <div>
          <dt>Confirmado em</dt>
          <dd>{formatarDataHora(lote.confirmadoEm)}</dd>
        </div>
      </dl>
    ),
  },
];

function classeDaLinha(lote: LoteDaSincronizacao): string | undefined {
  return lote.ultimoErro === null ? undefined : 'sincronizacao__linha--erro';
}

/**
 * Os lotes que o ERP entregou à sincronização. "Recebido" já foi gravado
 * aqui e ainda não teve o recebimento confirmado de volta ao ERP; a falha
 * nessa confirmação aparece na linha, com as tentativas.
 */
export function AbaLotes() {
  const [tipo, definirTipo] = useState(TODOS);
  const [filial, definirFilial] = useState('');
  const [status, definirStatus] = useState(TODOS);
  const [pagina, definirPagina] = useState(1);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeLotes>(
    () => ({
      pagina,
      ...seInformado('tipo', valorDoSeletor(tipo, TIPOS_DE_LOTE)),
      ...seInformado('codFil', codFil),
      ...seInformado('status', valorDoSeletor(status, STATUS_DO_LOTE)),
    }),
    [pagina, tipo, codFil, status],
  );

  const buscar = useCallback(() => listarLotes(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="sincronizacao__aba">
      <FaixaDeFiltros rotulo="Filtros dos lotes">
        <FiltroSelecao
          rotulo="Tipo"
          valor={tipo}
          opcoes={OPCOES_DE_TIPO}
          aoMudar={(valor) => {
            recortar(() => {
              definirTipo(valor);
            });
          }}
        />
        <CampoFilial
          valor={filial}
          aoMudar={(valor) => {
            recortar(() => {
              definirFilial(valor);
            });
          }}
        />
        <FiltroSelecao
          rotulo="Status do lote"
          valor={status}
          opcoes={OPCOES_DE_STATUS}
          aoMudar={(valor) => {
            recortar(() => {
              definirStatus(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'lote', 'lotes')}
          </ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando os lotes…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeFila tamanho={22} />}
            titulo="Nenhum lote neste recorte."
            detalhe="Cada leitura do ERP chega em lotes; eles aparecem aqui assim que a sincronização os recebe."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={COLUNAS}
            chaveDe={(lote) => lote.id}
            descricao="Lotes recebidos do ERP"
            classeDaLinha={classeDaLinha}
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'lote', 'lotes')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
